/**
 * Contact normalizer using Web Worker
 * Manages contact normalization off the main thread
 */

import { Contact, PhoneNumber } from './ContactStore';
import { normalizeForSearch } from '../utils/latvianNormalizer';

export interface ContactNormalizationMessage {
  type: 'normalize';
  contacts: any[];
  source: string;
  sourceType: 'corporate' | 'personal' | 'internal';
  requestId: string;
}

export interface ContactNormalizationResponse {
  type: 'normalized' | 'error';
  contacts?: Contact[];
  error?: string;
  requestId: string;
}

export class ContactNormalizer {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, {
    resolve: (contacts: Contact[]) => void;
    reject: (error: Error) => void;
  }>();

  constructor() {
    this.initWorker();
  }

  private initWorker(): void {
    try {
      // Create worker from the worker file
      this.worker = new Worker('./workers/ContactNormalizer.worker.js');

      this.worker.onmessage = (event: MessageEvent<ContactNormalizationResponse>) => {
        const { type, contacts, error, requestId } = event.data;
        const pending = this.pendingRequests.get(requestId);

        if (pending) {
          this.pendingRequests.delete(requestId);

          if (type === 'normalized' && contacts) {
            pending.resolve(contacts);
          } else if (type === 'error' && error) {
            pending.reject(new Error(error));
          }
        }
      };

      this.worker.onerror = (error) => {
        console.error('Contact normalizer worker error:', error);
        // Reject all pending requests
        this.pendingRequests.forEach(({ reject }) => {
          reject(new Error('Worker error'));
        });
        this.pendingRequests.clear();
      };
    } catch (error) {
      console.warn('Failed to create contact normalizer worker, falling back to main thread:', error);
      this.worker = null;
    }
  }

  async normalizeContacts(contacts: any[], source: string, sourceType: 'corporate' | 'personal' | 'internal' = 'corporate'): Promise<Contact[]> {
    if (!this.worker) {
      // Fallback to main thread normalization
      return this.normalizeContactsMainThread(contacts, source, sourceType);
    }

    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return new Promise<Contact[]>((resolve, reject) => {
      this.pendingRequests.set(requestId, { resolve, reject });

      const message: ContactNormalizationMessage = {
        type: 'normalize',
        contacts,
        source,
        sourceType,
        requestId
      };

      this.worker!.postMessage(message);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error('Contact normalization timeout'));
        }
      }, 30000);
    });
  }

  private normalizeContactsMainThread(contacts: any[], source: string, sourceType: 'corporate' | 'personal' | 'internal' = 'corporate'): Contact[] {
    return contacts.map(contact => this.normalizeContact(contact, source, sourceType));
  }

  private normalizeContact(contact: any, source: string, sourceType: 'corporate' | 'personal' | 'internal' = 'corporate'): Contact {
    const displayName = contact.displayName || 
      `${contact.givenName || ''} ${contact.surname || ''}`.trim() || 
      'Unknown Name';
    
    const givenName = contact.givenName || '';
    const surname = contact.surname || '';
    
    // For internal users, use 'mail' field only (no UPN fallback)
    const emailPrimary = sourceType === 'internal' 
      ? contact.mail || ''
      : contact.emailAddresses?.[0]?.address || '';
    
    const emails = sourceType === 'internal'
      ? contact.mail ? [contact.mail] : []
      : contact.emailAddresses?.map((e: any) => e.address) || [];
    
    
    const company = sourceType === 'internal' 
      ? contact.department || contact.companyName || contact.officeLocation || ''
      : contact.companyName || contact.department || '';
    const title = contact.jobTitle || '';
    
    const phones: PhoneNumber[] = [
      ...(contact.businessPhones || []).map((number: string) => ({ number, type: 'work' as const })),
      ...(contact.mobilePhone ? [{ number: contact.mobilePhone, type: 'mobile' as const }] : []),
      ...(contact.homePhones || []).map((number: string) => ({ number, type: 'home' as const }))
    ];

    return {
      id: contact.id,
      etag: contact['@odata.etag'] || contact.etag || '',
      source,
      sourceType,
      displayName,
      givenName,
      surname,
      emailPrimary,
      emails,
      company,
      title,
      phones,
      updatedAt: contact.lastModifiedDateTime || new Date().toISOString(),
      deleted: false,
      normalized: {
        nameLower: normalizeForSearch(displayName),
        givenNameLower: normalizeForSearch(givenName),
        surnameLower: normalizeForSearch(surname),
        emailLower: normalizeForSearch(emailPrimary),
        companyLower: normalizeForSearch(company),
      },
    };
  }

  destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingRequests.clear();
  }
}
